import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { AutoSizer } from 'react-virtualized';
import moment from 'moment';
import { FiEdit, FiInfo, FiSave } from 'react-icons/fi';
import { get } from 'lodash';
import { withRouter } from 'react-router-dom';
import { Classes, PopoverInteractionKind, Position } from '@blueprintjs/core';
import styled, { css } from 'styled-components';
import { Collection } from 'core/model';
import {
  BorderedTab,
  BorderedTabs,
  Box,
  Button,
  ButtonGroup,
  Callout,
  Card,
  EmptyState,
  Flex,
  Heading,
  Icon,
  Link,
  LinkButton,
  Popover,
  Spinner,
  Suspense,
  Tag,
  Text,
  Tooltip
} from 'core/components';
import { showSuccessToast, showErrorToast } from 'core/components/toast';
import { CELL_TYPES, Search, Table } from 'core/components/table';
import LabelList from 'app/components/labels/LabelList';
import { RemoveButton } from 'core/form';
import CodeMirror from 'core/components/CodeMirror';
import { DEFAULT_DATE_FORMAT } from 'core/util/dateUtils';
import { ReactComponent as AgentStatusUpgradeAvailableIcon } from 'app/assets/agents/agent-status-upgrade.svg';
import storeLoader from 'app/stores/storeLoader';
import Map from 'app/components/map';
import SiteLink from 'app/components/links/SiteLink';
import { CLOUD_PROVIDERS } from 'app/util/constants';
import TestCollection from 'app/stores/synthetics/TestCollection';
import TestAlerts from 'app/views/synthetics/components/incidentLogs/TestAlerts';
import { SIDEBAR_WIDTH } from 'app/views/synthetics/components/incidentLogs/constants';
import AgentDowntime from 'app/views/synthetics/components/agentDowntime/AgentDowntime';
import Validator from 'validatorjs';
import AgentConfigFormDialog from './AgentConfigFormDialog';
import AgentMigrateFormDialog from './AgentMigrateFormDialog';
import UpdateConfigAgentFormDialog from './UpdateConfigAgentFormDialog';
import { getAgentRemovalBlockingTests } from '../utils/agentRemoval';
import { getTestTypeLabel } from '../utils/syntheticsUtils';

export const AGENT_ACTIONS = {
  POPOUT: 'popout',
  ACTIVATE: 'activate',
  REMOVE_PENDING: 'removePending',
  CONFIGURE: 'configure',
  REMOVE: 'remove',
  MIGRATE_AGENT: 'migrateAgent',
  MIGRATE_TESTS: 'migrateTests',
  ADD_CLUSTER_WORKERS: 'addClusterWorkers',
  SAVE_SETTINGS: 'saveSettings'
};

const AgentDetailsWrapper = styled(Flex)`
  width: ${SIDEBAR_WIDTH}px;

  .button-box {
    width: auto;

    button {
      min-width: 85px;
    }
  }

  .test-search-bar {
    background: ${({ theme }) => theme.colors.appBackground};
  }

  ${({ fullSize }) =>
    fullSize &&
    css`
      width: auto;

      .button-box {
        width: ${SIDEBAR_WIDTH}px;
      }

      .agent-summary {
        flex-direction: row;

        .map-box {
          flex: 2;
        }
      }

      .activate-box {
        width: 500px;
      }
    `}
`;

const StyledWrapper = styled(Box)`
  .CodeMirror {
    height: 300px;
    width: 650px;
    max-width: 100%;
  }
`;

const agentContextRules = {
  browserVersion: 'string',
  maxConcurrentBrowserTests: 'integer',
  scamperUdpProbe: 'boolean',
  extraScamperArgs: 'string',
  scamperTraceArgs: 'string',
  scamperPingArgs: 'string',
  vaultGroupNameRegex: 'string'
};

@storeLoader({ store: '$kentikAgents', force: true })
@inject('$auth', '$dictionary', '$syn')
@withRouter
@observer
class AgentDetails extends Component {
  static defaultProps = {
    // fullSize: false is rendering in a sidebar in agent management
    // fullSize: true is rendering as a distinct agent details page
    fullSize: false,
    // Which detail tabs to render; callers (e.g. the synth agent drawer) can show a subset.
    visibleTabs: ['summary', 'tests', 'alerts', 'downtime', 'settings'],
    // Which actions to render; callers can show a subset (e.g. the agents table keeps the common ones in a kebab).
    visibleActions: Object.values(AGENT_ACTIONS),
    // Hide the pop-out link and action buttons (configure/migrate/remove/cluster) for read-only surfaces.
    hideActions: false
  };

  state = {
    loadingDetail: true,
    currentDetail: null,
    testCollection: new Collection([], {
      groupBy: 'test_type',
      sortState: {
        field: 'test',
        direction: 'asc'
      },
      getSortValue: (field, model) => {
        const value = model.get(field);

        if (field === 'test') {
          return model.get('display_name', `Test ${model.id}`).toLowerCase();
        }

        if (field === 'test_type') {
          return getTestTypeLabel(value);
        }

        return value;
      }
    }),
    isAgentConfigDialogOpen: false,
    isAgentMigrateDialogOpen: false,
    agentRemovalBlockers: null,
    updateConfigAgent: false,
    currentTabId: 'summary',
    agentContext: JSON.stringify({}, null, 2),
    agentContextError: false,
    agentContextErrorMessage: '',
    savingAgentContext: false,
    removingTestId: null // the model id of the test currently being removed or null if no removal is in progress
  };

  getAgentRemovalBlockingTests(agentDetails) {
    return getAgentRemovalBlockingTests(agentDetails);
  }

  showsAction(action) {
    const { hideActions, visibleActions } = this.props;
    return !hideActions && visibleActions.includes(action);
  }

  refreshDetails = () => {
    const { model, $syn } = this.props;
    const { testCollection } = this.state;
    $syn.requests.getAgentDetails(model.id).then((details) => {
      testCollection.processData(details.tests);

      this.setState(
        {
          loadingDetail: false,
          currentDetail: details,
          agentRemovalBlockers: this.getAgentRemovalBlockingTests(details),
          removingTestId: null
        },
        () => this.updateMarkers()
      );
    });
  };

  componentDidMount() {
    const { model, $syn, $auth, fullSize } = this.props;
    if (model) {
      this.setState({ loadingDetail: true, agentRemovalBlockers: null }, () => {
        this.refreshDetails();
      });
    }

    if ($auth.hasSudo && model && fullSize) {
      $syn.requests.fetchAgentContext(model.get('id')).then((context) => {
        this.setState({ agentContext: JSON.stringify(context, null, 2) });
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { model } = this.props;
    if (model && prevProps.model !== model) {
      setTimeout(() => {
        this.setState({ loadingDetail: true, agentRemovalBlockers: null }, () => {
          this.refreshDetails();
        });
      }, 0);
    }
  }

  handleAgentConfigDialogClose = () => {
    this.setState({ isAgentConfigDialogOpen: false });
  };

  handleTestEdit = (model) => {
    const { history } = this.props;
    history.push(`/v4/synthetics/tests/${model.id}`);
  };

  handleTestRemove(model) {
    this.setState({ removingTestId: model.id }, () => {
      model.destroy().finally(() => {
        const { $syn } = this.props;
        this.refreshDetails(); // removingTestId is reset here
        // refresh tests/testBurnRates, but don't wait for it
        $syn.tests.fetch({ force: true }).then(() => $syn.plan.syncTestCreditBurnRates());
      });
    });
  }

  handleTabChange = (currentTabId) => {
    const { onTabChange } = this.props;

    // keep track of the current tab
    // if we "pop out" to a full view, we'll want to jump into the same tab we left
    this.setState({ currentTabId }, () => {
      if (onTabChange) {
        onTabChange(currentTabId);
      }
    });
  };

  handleSave = () => {
    const { onSave } = this.props;

    if (onSave) {
      onSave();
    }

    this.refreshDetails();
  };

  getColumns = () => [
    {
      label: 'Test',
      name: 'display_name',
      flex: 1,
      ellipsis: false,
      renderer: ({ model }) => (
        <Box overflow="hidden">
          <Text as="div" muted ellipsis>
            {getTestTypeLabel(model.get('test_type'))}
          </Text>
          <Link to={`/v4/synthetics/tests/${model.id}/results`} fontWeight="bold" display="block" ellipsis>
            {model.get('display_name')}
          </Link>
        </Box>
      )
    },
    {
      label: 'Agents',
      name: 'config.agents.length',
      width: 75,
      ellipsis: false
    },
    {
      type: CELL_TYPES.ACTION,
      actions: [
        (model) => (
          <Button
            key="edit"
            icon={FiEdit}
            title="Edit"
            onClick={(e) => {
              e.stopPropagation();
              this.handleTestEdit(model);
            }}
            small
            minimal
          />
        ),
        (model) => {
          const { removingTestId } = this.state;
          return (
            <RemoveButton
              model={model}
              key="remove"
              onRemove={() => this.handleTestRemove(model)}
              entityName="test"
              disabled={removingTestId !== null && removingTestId !== model.id}
              icon={removingTestId === model.id ? <Spinner /> : 'trash'}
              showIcon
              small
              hideText
            />
          );
        }
      ]
    }
  ];

  handleCodeMirrorChange = (editor, data, value) => {
    try {
      const json = JSON.parse(value);
      const validation = new Validator(json, agentContextRules);
      if (validation.passes()) {
        this.setState({ agentContextError: false });
      } else {
        const errors = validation.errors.all();
        const errorMessages = Object.values(errors).flat();
        const errorMessageString = errorMessages.join('\n');
        this.setState({ agentContextError: true, agentContextErrorMessage: errorMessageString });
      }
    } catch (err) {
      this.setState({ agentContextError: true, agentContextErrorMessage: err.message });
    } finally {
      this.setState({ agentContext: value });
    }
  };

  handleCodeMirrorSave = () => {
    const { agentContext } = this.state;
    const { $syn, model } = this.props;
    this.setState({ savingAgentContext: true });
    try {
      const json = JSON.parse(agentContext);
      const prunedData = Object.keys(agentContextRules)
        .filter((key) => key in agentContextRules)
        .reduce((obj, key) => {
          if (json[key] !== undefined) {
            obj[key] = json[key];
          }

          return obj;
        }, {});
      $syn.requests.updateAgentContext(model.get('id'), prunedData).then((context) => {
        this.setState({ agentContext: JSON.stringify(context, null, 2) });
        showSuccessToast('Agent settings saved');
        this.setState({ savingAgentContext: false });
      });
    } catch (err) {
      showErrorToast('Error saving agent settings. Please check the JSON and try again.');
      this.setState({ savingAgentContext: false });
    }
  };

  onMapLoad = (map, mapCmp) => {
    this.map = map;
    this.mapCmp = mapCmp;
    setTimeout(() => {
      this.map.resize();
    }, 100);
  };

  updateMarkers = () => {
    if (this.mapCmp) {
      this.mapCmp.removeMarkers();
      this.mapCmp.addMarkers();
    }
  };

  getMarkers() {
    const { model } = this.props;

    if (model) {
      const { lat, lon } = model.geoCoords;
      if (lat || lon) {
        return [
          {
            id: model.id,
            lat,
            lon,
            title: model.label,
            icon: model.iconPng,
            modelId: model.id
          }
        ];
      }
    }
    return [];
  }

  renderLocation() {
    const { $dictionary, model } = this.props;
    const site = model.get('site');
    const cloud_provider = model.get('metadata.cloud_provider');

    const cloudLogo = () => {
      if (cloud_provider) {
        const cloudRegions = $dictionary.getCloudMetadataKeyToNameMap(cloud_provider);
        const cloudRegion = model.get('metadata.cloud_region');
        const { logoCmp: Logo } = CLOUD_PROVIDERS.byId(cloud_provider) || {};

        return (
          <Flex alignItems="flex-start">
            {Logo && <Logo width={14} height={14} style={{ marginRight: 4, marginTop: 2 }} />}
            <Box>{cloudRegions[cloudRegion] ? `${cloudRegion} (${cloudRegions[cloudRegion]})` : cloudRegion}</Box>
          </Flex>
        );
      }
      return null;
    };

    return (
      <>
        <Text fontWeight="heavy" as="div">
          Location
        </Text>
        {site && site.id && <SiteLink siteId={site.id}>{site.title}</SiteLink>}
        {cloudLogo()}
        <Text as="div" muted>
          {model.locationString}
        </Text>
      </>
    );
  }

  renderPublicIPs = () => {
    const { model } = this.props;
    const agentFamily = model.get('agent_family');
    const agentType = model.get('agent_type');
    const privateIPv4 = model.get('metadata.private_ipv4_addresses[0].value');
    const privateIPv6 = model.get('metadata.private_ipv6_addresses[0].value');
    const publicIPv4 = model.get('metadata.public_ipv4_addresses[0].value');
    const publicIPv6 = model.get('metadata.public_ipv6_addresses[0].value');
    const local_ip = model.get('local_ip');
    const ip = model.get('ip');
    const privateIPs = [];
    const publicIPs = [];
    const showBothIPs = agentFamily === 'DUAL';
    const showIPv4 = showBothIPs || agentFamily === 'v4';
    const showIPv6 = showBothIPs || agentFamily === 'v6';

    if (showIPv4) {
      if (privateIPv4) {
        privateIPs.push(privateIPv4);
      }

      if (publicIPv4) {
        publicIPs.push({ value: publicIPv4, family: 'v4' });
      }
    }

    if (showIPv6) {
      if (privateIPv6) {
        privateIPs.push(privateIPv6);
      }

      if (publicIPv6) {
        publicIPs.push({ value: publicIPv6, family: 'v6' });
      }
    }

    if (privateIPs.length === 0) {
      privateIPs.push(local_ip);
    }

    if (publicIPs.length === 0 && ip) {
      publicIPs.push({ value: ip, family: agentFamily === 'v6' ? 'v6' : 'v4' });
    }

    const { staticIpFamilies } = model;

    return (
      <Box mb={2}>
        {publicIPs.length > 0 && (
          <>
            <Box>
              <Text fontWeight="heavy">Public IP</Text>
              {agentType === 'private' && (
                <Tooltip
                  position={Position.TOP}
                  content={
                    <div style={{ maxWidth: 200 }}>
                      Agent&apos;s public IP change could take up to 24h or until next agent restart to show up in the
                      UI.
                    </div>
                  }
                >
                  <Icon icon={FiInfo} color="muted" mx="4px" />
                </Tooltip>
              )}
            </Box>
            {publicIPs.map(({ value, family }) => (
              <Flex key={value} alignItems="center" gap="4px">
                <Text muted as="div">
                  {value}
                </Text>
                {!staticIpFamilies.has(family) && (
                  <Tag minimal small borderRadius="24px">
                    Dynamic
                  </Tag>
                )}
              </Flex>
            ))}
          </>
        )}

        {agentType === 'private' && privateIPs.length > 0 && (
          <Box mt={2}>
            <Text fontWeight="heavy" as="div">
              Private IP
            </Text>
            {privateIPs.map((privateIP) => (
              <Text muted as="div" key={privateIP}>
                {privateIP}
              </Text>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  get testsEmptyState() {
    return <EmptyState description="No tests configured with this agent" icon="inbox" />;
  }

  get testCollectionColumns() {
    return [
      {
        label: 'Test',
        name: 'test',
        flexBasis: 150,
        renderer: ({ model: testModel }) => (
          <Link to={`/v4/synthetics/tests/${testModel.id}/results`}>
            {testModel.get('display_name', `Test ${testModel.id}`)}
          </Link>
        )
      }
    ];
  }

  // when rendered in full size view, the tabs will be controlled by props from the wrapping page
  get tabProps() {
    const { fullSize, selectedTabId } = this.props;

    if (fullSize) {
      return { selectedTabId };
    }

    return {};
  }

  groupSummary = ({ groupKey }) => <Text as="div">{getTestTypeLabel(groupKey)}</Text>;

  renderBlockAgentRemoval = ({ collection }) => (
    <Box p={3} width={500}>
      <Callout mb={2}>
        <Flex alignItems="center">
          <Icon iconSize={35} icon="disable" color="warning" />
          <Box ml={1} flex="1 1 auto">
            <Text muted fontWeight="bold">
              Agent removal would impact the integrity of active tests. The tests below must be updated or removed
              before this agent can be removed.
            </Text>
          </Box>
        </Flex>
      </Callout>
      <Box maxHeight={300} overflow="auto" flex={1}>
        <Table
          collection={collection}
          selectOnRowClick={false}
          columns={this.getColumns()}
          emptyState={<EmptyState icon="inbox" title="Test list empty" />}
        />
      </Box>
    </Box>
  );

  render() {
    const { model, $syn, $auth, drawerOnClose, fullSize, history, location, addAgentCluster, loading, visibleTabs } =
      this.props;
    const {
      loadingDetail,
      currentDetail,
      testCollection,
      isAgentConfigDialogOpen,
      isAgentMigrateDialogOpen,
      agentRemovalBlockers,
      updateConfigAgent,
      currentTabId,
      agentContext,
      agentContextError,
      agentContextErrorMessage,
      savingAgentContext
    } = this.state;

    if (!model) {
      return null;
    }

    const blockingTestCollection = new TestCollection(agentRemovalBlockers || []);
    const removalAllowed = !blockingTestCollection.size;
    const removalDisabled = loading || loadingDetail || !removalAllowed; // the agent removal button is disabled when loading details or there are blocking tests
    const removalDetailsDisabled = loadingDetail || removalAllowed; // the removal details popover is disabled when loading details or removal is allowed
    const currentTestCount = get(currentDetail, 'tests.length', 0);

    const { id, agent_alias, agent_status, agent_type, agent_version, metadata, os } = model.get();

    const { isUpdateAvailable } = model;
    const { activationSummary } = metadata || {};
    const markers = this.getMarkers();
    const isGlobal = agent_type === 'global';
    const agentAlertingEnabled = currentDetail?.agent_alert_rule_config?.alertingRuleId;

    const showConfigure = this.showsAction(AGENT_ACTIONS.CONFIGURE) && model.canEdit;
    const showRemove = this.showsAction(AGENT_ACTIONS.REMOVE) && model.canDelete;
    const showMigrateAgent = this.showsAction(AGENT_ACTIONS.MIGRATE_AGENT) && isGlobal && $auth.hasSudo;
    const showMigrateTests = this.showsAction(AGENT_ACTIONS.MIGRATE_TESTS) && isGlobal && $auth.hasSudo;
    // don't render an empty bordered group when every button in it is hidden
    const showButtonGroup =
      agent_status === 'OK' && (showConfigure || showRemove || showMigrateAgent || showMigrateTests);

    return (
      <AgentDetailsWrapper flexDirection="column" p={3} height="100%" fullSize={fullSize}>
        <Flex justifyContent="space-between">
          {!isGlobal || $auth.hasSudo || $auth.isSpoofed ? (
            <Text as="div" muted mb="4px" small>
              ID: {id}
            </Text>
          ) : (
            <div />
          )}

          {/* offer a way to "pop out" into the full-size view */}
          {!fullSize && this.showsAction(AGENT_ACTIONS.POPOUT) && (
            <LinkButton
              mb={1}
              icon="maximize"
              small
              minimal
              to={`/v4/synthetics/agents/${model.id}/${currentTabId}${location.search}`}
            />
          )}
        </Flex>
        <Flex alignItems="center" mb={1}>
          <Icon icon={model.icon} mr="4px" iconSize={20} />
          <Heading level={3} fontWeight="heavy" m={0}>
            {agent_alias || id}
          </Heading>
        </Flex>
        <LabelList
          labels={model.labels}
          mt="-8px"
          mb="12px"
          priorityLabels={model.collection.discreteFilterValues.labelMultiSelect}
          maxLabelsToShow={10}
          wrapLabels
        />

        {this.showsAction(AGENT_ACTIONS.ACTIVATE) && model.canEdit && agent_status === 'WAIT' && (
          <Callout intent="primary" mb={2} className="activate-box">
            <Box>This agent is not yet ready for use. Click below to finish registration.</Box>
            <Button
              text={agent_status === 'WAIT' ? 'Activate' : 'Configure'}
              onClick={() => this.setState({ isAgentConfigDialogOpen: true })}
              intent={agent_status === 'WAIT' ? 'primary' : 'none'}
              alignSelf="flex-end"
              width={125}
              mt={1}
            />
          </Callout>
        )}
        {this.showsAction(AGENT_ACTIONS.REMOVE_PENDING) &&
          $auth.hasSudo &&
          agent_status === 'WAIT' &&
          model.canDelete && (
            <Flex mb={2}>
              <RemoveButton
                entityName="agent"
                model={model}
                minimal={false}
                showIcon={false}
                onRemove={() =>
                  model.destroy().then(() => {
                    if (drawerOnClose) {
                      drawerOnClose();
                    }
                    if (fullSize) {
                      history.push('/v4/synthetics/agents');
                    } else {
                      $syn.agents.remove(model.id);
                    }
                    return undefined;
                  })
                }
                popoverProps={{
                  targetTagName: 'div',
                  fill: true,
                  confirmBodyContent: (
                    <Box maxWidth={375}>
                      <Text>
                        Are you sure you want to permanently delete this pending agent? This action cannot be reversed!
                      </Text>
                    </Box>
                  )
                }}
              />
            </Flex>
          )}
        {showButtonGroup && (
          <Flex alignItems="center" mb={1} justifyContent="space-between" className="button-box">
            <ButtonGroup>
              {showConfigure && (
                // disable editing until loading is done so we can guarantee any agents with universal agent dependencies
                // have locked agent name and site fields in the config form
                <Button
                  text="Configure"
                  disabled={loading}
                  onClick={() => this.setState({ isAgentConfigDialogOpen: true })}
                />
              )}
              {showRemove && (
                <Popover
                  position={Position.BOTTOM_LEFT}
                  disabled={removalDetailsDisabled}
                  content={this.renderBlockAgentRemoval({ collection: blockingTestCollection })}
                  interactionKind={PopoverInteractionKind.HOVER}
                >
                  <RemoveButton
                    key="remove"
                    entityName="agent"
                    disabled={removalDisabled}
                    model={model}
                    minimal={false}
                    showIcon={false}
                    buttonProps={{
                      rightIcon: removalDisabled ? 'info-sign' : null,
                      // when synth is a universal agent capability, bypass removal confirmation and just go directly to ua agent detail view
                      onClick: () =>
                        model.universalAgent
                          ? history.push('/v4/settings/universal-agents', { agentId: model.universalAgent.id })
                          : null
                    }}
                    onRemove={() =>
                      // when synth is a legacy agent (not a universal agent capability), proceed with normal removal of the agent
                      !model.universalAgent &&
                      model.destroy().then(() => {
                        if (drawerOnClose) {
                          drawerOnClose();
                        }
                        // refresh tests, but don't wait for it
                        $syn.tests.fetch({ force: true }).then(() => $syn.plan.syncTestCreditBurnRates());

                        if (fullSize) {
                          history.push('/v4/synthetics/agents');
                        } else {
                          // the full size view uses the $syn.agents collection so collection removal is handled already
                          // however when in the agent mgmt sidebar, each agent type is in their own temp collection created on the fly so we'll remove from the $syn.agents collection manually in order to show the correct state
                          $syn.agents.remove(model.id);
                        }

                        return undefined;
                      })
                    }
                    popoverProps={{
                      targetTagName: 'div',
                      fill: true,
                      confirmBodyContent: (
                        <Box maxWidth={375}>
                          {isGlobal && (
                            <Text>
                              <Text fontWeight="heavy" color="danger">
                                WARNING:
                              </Text>{' '}
                              Deleting this GLOBAL agent{' '}
                              <Text fontWeight="heavy" color="warning">
                                may have cross-company effects
                              </Text>
                              , potentially leaving tests in invalid states (Tests shown here for this agent apply ONLY
                              to this company).{' '}
                              <Text fontWeight="heavy">Are you sure you want to permanently delete this agent?</Text>
                            </Text>
                          )}
                          {!isGlobal && !currentTestCount && (
                            <Text>
                              Are you sure you want to permanently delete this agent? This action cannot be reversed!
                            </Text>
                          )}
                          {!isGlobal && !!currentTestCount && (
                            <Text>
                              <Text fontWeight="heavy" color="warning">
                                WARNING:
                              </Text>{' '}
                              Deleting this agent will affect {currentTestCount} test
                              {currentTestCount > 1 ? 's' : ''}. Are you sure you want to permanently delete this agent?
                            </Text>
                          )}
                        </Box>
                      )
                    }}
                  />
                </Popover>
              )}
              {showMigrateAgent && (
                <Button
                  disabled={loadingDetail}
                  onClick={() => this.setState({ isAgentMigrateDialogOpen: true, updateConfigAgent: false })}
                  text="Migrate Agent"
                />
              )}

              {showMigrateTests && (
                <Button
                  disabled={loadingDetail}
                  onClick={() => this.setState({ isAgentMigrateDialogOpen: true, updateConfigAgent: true })}
                  text="Migrate Tests"
                />
              )}
            </ButtonGroup>
          </Flex>
        )}
        {this.showsAction(AGENT_ACTIONS.ADD_CLUSTER_WORKERS) && agent_status === 'OK' && isGlobal && $auth.hasSudo && (
          <Flex alignItems="center" mb={2}>
            <Button
              intent="primary"
              disabled={loadingDetail}
              onClick={() => addAgentCluster(id)}
              text="Add Cluster Workers"
            />
          </Flex>
        )}

        <BorderedTabs {...this.tabProps} onChange={this.handleTabChange} minimal>
          {visibleTabs.includes('summary') && (
            <BorderedTab
              id="summary"
              title="Agent"
              panel={
                <Flex flexDirection="column" className="agent-summary" role="tabpanel">
                  <Box flex={1}>
                    {(!isGlobal || $auth.hasSudo) && (
                      <Box mb={2}>
                        <Text fontWeight="heavy" as="div">
                          Version
                        </Text>
                        <Flex>
                          <Text muted> {agent_version}</Text>
                          {isUpdateAvailable && (
                            <Tag intent="primary" minimal round ml="4px">
                              <Flex
                                as="a"
                                alignItems="center"
                                href="https://kb.kentik.com/docs/kentik-synthetics-agents"
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                <Icon
                                  mr="4px"
                                  icon={AgentStatusUpgradeAvailableIcon}
                                  title="Upgrade Available"
                                  iconSize={14}
                                  cursor="pointer"
                                />
                                <Text as="div" fontWeight="heavy" mr="4px">
                                  Update Available
                                </Text>
                                ({$syn.plan.latestAgentVersion})
                              </Flex>
                            </Tag>
                          )}
                        </Flex>
                      </Box>
                    )}

                    {this.renderPublicIPs()}

                    <Box mb={2}>
                      <Text fontWeight="heavy" as="div">
                        Host OS
                      </Text>
                      <Text muted>{os || 'Unknown'}</Text>
                    </Box>

                    <Box mb={2}>
                      <Text fontWeight="heavy" as="div">
                        ASN
                      </Text>
                      <Text muted>{model.asFormatted || 'Unknown'}</Text>
                    </Box>

                    {activationSummary && (
                      <>
                        <Box mb={2}>
                          <Text fontWeight="heavy" as="div">
                            Activated
                          </Text>
                          <Text muted>
                            {moment.utc(activationSummary.activationDate).format(DEFAULT_DATE_FORMAT)} (
                            {activationSummary.user_full_name})
                          </Text>
                        </Box>
                      </>
                    )}
                    <Box mb={1}>{this.renderLocation()}</Box>
                  </Box>
                  <Box flex={1} minHeight={200} mb={1} className="map-box">
                    <AutoSizer disableHeight>
                      {({ width }) => {
                        if (width === 0) {
                          return null;
                        }
                        const height = Math.max(200, (400 / 840) * width);
                        return (
                          <Card width={width} height={height} overflow="hidden">
                            <Map
                              containerWrapperProps={{ width, height }}
                              onLoad={this.onMapLoad}
                              markers={markers}
                              autoBounds
                              maxZoom={fullSize ? 5 : 1}
                              onMarkerClick={() => {}}
                              renderWorldCopies
                            />
                          </Card>
                        );
                      }}
                    </AutoSizer>
                  </Box>
                </Flex>
              }
            />
          )}
          {visibleTabs.includes('tests') && (
            <BorderedTab
              id="tests"
              title="Tests"
              panel={
                <>
                  <Suspense
                    loading={loadingDetail}
                    fallback={
                      <Box pt={2}>
                        <Spinner size={24} />
                      </Box>
                    }
                  >
                    {currentDetail && (
                      <Card mb={1} overflow="auto">
                        <Box position="sticky" top={0} p={1} zIndex={1} className="test-search-bar">
                          <Search
                            collection={testCollection}
                            inputProps={{ small: true }}
                            css={{
                              [`.${Classes.INPUT}:focus`]: { boxShadow: 'none' }
                            }}
                          />
                        </Box>
                        <Table
                          collection={testCollection}
                          columns={this.testCollectionColumns}
                          emptyState={this.testsEmptyState}
                          groupSummaryLookup={this.groupSummary}
                          stickyHeaderOffset={40}
                          stickyHeader
                          stickyGroups
                        />
                      </Card>
                    )}
                  </Suspense>
                </>
              }
            />
          )}
          {visibleTabs.includes('alerts') && (
            <BorderedTab
              id="alerts"
              title="Alerts"
              panel={
                currentDetail?.tests.length > 0 ? (
                  <TestAlerts flex={1} overflow="hidden" agent={currentDetail} minimalTable={!fullSize} />
                ) : (
                  <div>{this.testsEmptyState}</div>
                )
              }
            />
          )}
          {visibleTabs.includes('downtime') && (
            <BorderedTab
              id="downtime"
              title={
                <Tooltip content="Agent alerting is disabled" disabled={agentAlertingEnabled} position={Position.TOP}>
                  Downtime
                </Tooltip>
              }
              disabled={!agentAlertingEnabled}
              panel={<AgentDowntime agent={currentDetail} />}
            />
          )}
          {visibleTabs.includes('settings') &&
          this.showsAction(AGENT_ACTIONS.SAVE_SETTINGS) &&
          $auth.hasSudo &&
          fullSize ? (
            <BorderedTab
              id="settings"
              title="Settings"
              panel={
                <StyledWrapper role="tabpanel">
                  <div style={{ marginBottom: '10px' }}>
                    <a
                      href="https://www.notion.so/kentik/Agent-Internal-Settings-06a1342ef0274c798d7578da166af20f?pvs=4"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      How To Use These Settings
                    </a>
                  </div>

                  <CodeMirror
                    style={{ height: '200px' }}
                    options={{ lineNumbers: true, tabSize: 2, mode: { name: 'javascript', json: true } }}
                    value={agentContext}
                    onChange={this.handleCodeMirrorChange}
                  />
                  {agentContextError && (
                    <Text style={{ display: 'block' }} color="danger">
                      {agentContextErrorMessage}
                    </Text>
                  )}
                  <Button
                    text="Save Settings"
                    intent="primary"
                    key="save-settings"
                    icon={FiSave}
                    title="Save"
                    onClick={this.handleCodeMirrorSave}
                    mt={1}
                    disabled={agentContextError || savingAgentContext}
                  />
                </StyledWrapper>
              }
            />
          ) : null}
        </BorderedTabs>

        {isAgentConfigDialogOpen && (
          <AgentConfigFormDialog
            model={model}
            universalAgentModel={model.universalAgent}
            onSave={this.handleSave}
            onClose={this.handleAgentConfigDialogClose}
          />
        )}

        {isAgentMigrateDialogOpen && !updateConfigAgent && (
          <AgentMigrateFormDialog
            model={model}
            refreshDetails={() => this.refreshDetails()}
            onClose={() => this.setState({ isAgentMigrateDialogOpen: false })}
          />
        )}

        {isAgentMigrateDialogOpen && updateConfigAgent && (
          <UpdateConfigAgentFormDialog
            model={model}
            refreshDetails={() => this.refreshDetails()}
            onClose={() => this.setState({ isAgentMigrateDialogOpen: false })}
          />
        )}
      </AgentDetailsWrapper>
    );
  }
}

export default AgentDetails;
